import {
  removeTransactionAttachment,
  setTransactionAttachments,
} from "@/state/transactions/CurrentTransactionEvents";
import { File } from "@/type/core/dashboard/dashboard.types";
import { ArrayHelper } from "@/utils/helpers/array-helper";
import SubmitButton from "@components/core/buttons/SubmitButton";
import ImportFilesCard from "@components/core/cards/ImportFilesCard";
import GenericSortableTable from "@components/core/tables/GenericSortableTable";
import {
  MAX_SIZE_ALL_FILES,
  MAX_SIZE_PER_FILE,
  SIGNATURE_MAX_FILES,
  formValidateTriggers,
} from "@utils/Constant";
import { columns } from "@views/core/extranet/transactions/signature/columns/FilesColumns";
import { Col, Form, Row } from "antd";
import { useForm } from "antd/lib/form/Form";
import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { SignatureScreenSectionProps } from "@views/core/extranet/transactions/signature/SignatureScreen";
import { FileHelper } from "@utils/helpers/file-helper";

interface OfficeChoiceSectionProps extends SignatureScreenSectionProps {
  description: string;
  descriptionSeparator: string;
  descriptionUrlTitle: string;
  isFromModel?: boolean;
  className?: string;
  attachments: File[];
  currentFileCount: number;
}

const AttachmentsSection: FunctionComponent<OfficeChoiceSectionProps> = ({
  onSubmit,
  description,
  descriptionSeparator,
  descriptionUrlTitle,
  isFromModel = false,
  className = "",
  attachments,
  currentFileCount,
}) => {
  const { t } = useTranslation();
  const [form] = useForm();

  const handleSubmit = () => {
    onSubmit?.();
  };

  const handleSetAttachments = (newFiles: File[]) => {
    setTransactionAttachments(
      FileHelper.removeNameDoublon([...attachments, ...newFiles]),
    );
  };

  return (
    <Row
      className={`app-section-attachments justify-content-center ${className}`}
    >
      <Col span={24} lg={20} xl={16}>
        <Form
          form={form}
          onFinish={handleSubmit}
          {...formValidateTriggers}
          layout="vertical"
        >
          {attachments && attachments.length > 0 ? (
            <GenericSortableTable<File>
              data={attachments}
              setData={setTransactionAttachments}
              columns={columns(
                attachments,
                setTransactionAttachments,
                removeTransactionAttachment,
              )}
              className="table-no-header table-light-grey mb-16"
            />
          ) : (
            <div className="mb-16" />
          )}
          <ImportFilesCard
            description={description}
            descriptionSeparator={descriptionSeparator}
            descriptionUrlTitle={descriptionUrlTitle}
            url="#"
            accept="application/pdf"
            maxSize={MAX_SIZE_PER_FILE}
            maxSizeOfAllFiles={MAX_SIZE_ALL_FILES}
            extensions={["pdf"]}
            name="dropZoneCardItem"
            openFilesSelectionOnClick
            setFiles={handleSetAttachments}
            currentDocumentsSize={ArrayHelper.sumElements(
              attachments.map((attachment) => attachment.size ?? 0),
            )}
            currentFileCount={currentFileCount}
            maxCount={SIGNATURE_MAX_FILES} // SIGNATURE_MAX_FILES because attachments are only for signatures and not for sealings
          />
          <div className="d-flex align-items-center justify-content-end flex-wrap gap-16 pt-4">
            {!isFromModel && attachments.length === 0 && (
              <div className="flex-grow-1">
                <a
                  onClick={() => form.submit()}
                  className="cursor-pointer"
                  data-test="link"
                  data-alt={t("signature.accordion.attachmentContinueLink")}
                >
                  {t("signature.accordion.attachmentContinueLink")}
                </a>
              </div>
            )}
            <SubmitButton
              disabled={!isFromModel && attachments.length === 0}
              label={isFromModel ? "buttons.next" : "buttons.validate"}
              dataAlt={t("buttons.validate")}
              className="mw-160"
            />
          </div>
        </Form>
      </Col>
    </Row>
  );
};

export default AttachmentsSection;
